import type { BrandPresignedUploadResponse } from "@nlc/shared-types";

import { useCallback, useState } from "react";

import apiClient from "@/lib/api/client";

type UploadType = "guideline" | "logo";

interface UploadState {
  error: null | string;
  isUploading: boolean;
}

export function useBrandPresignedUpload(): {
  error: null | string;
  isUploading: boolean;
  upload: (file: File, type: UploadType) => Promise<null | string>;
} {
  const [state, setState] = useState<UploadState>({
    error: null,
    isUploading: false,
  });

  const upload = useCallback(
    async (file: File, type: UploadType): Promise<null | string> => {
      setState({ error: null, isUploading: true });

      try {
        const { fields, s3Key, uploadUrl } = await apiClient
          .url("/brands/presigned-upload")
          .query({
            contentType: file.type || "application/octet-stream",
            fileName: file.name,
            fileSize: file.size,
            type,
          })
          .get()
          .json<BrandPresignedUploadResponse>();

        const formData = new FormData();

        for (const [key, val] of Object.entries(fields))
          formData.append(key, val);
        formData.append("file", file);

        const response = await fetch(uploadUrl, {
          body: formData,
          method: "POST",
        });

        if (!response.ok) {
          throw new Error("Upload failed");
        }

        setState({ error: null, isUploading: false });

        return s3Key;
      } catch (error) {
        const message =
          (error as { message?: string })?.message ?? "Upload failed";

        setState({ error: message, isUploading: false });

        return null;
      }
    },
    [],
  );

  return {
    error: state.error,
    isUploading: state.isUploading,
    upload,
  };
}
